/* eslint-disable camelcase */
import React from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { array, object, bool, string } from "prop-types";
import { Link } from "@quintype/components";
import { HeroImage } from "../../atoms/HeroImage";
import { PublishAndReadTime } from "../../atoms/PublishAndReadTime";
import { getStoryHeadline, getStoryLabel, isExternalStory } from "../../utils/story";
import CollectionTitle from "../../atoms/CollectionTitle";
import { DfpComponent } from "../../ads/dfp-component";
import { Label } from "../../atoms/Label";
import Headline from "../../atoms/Headline";
import SubHeadline from "../../atoms/SubHeadline";
import { RowSeparator } from "../../atoms/RowSeparator";

import "./three-col-three-stories.m.css";

const StoryCard = ({ story, showSeparator, showTimestamp, showReadTime, showSubHeadline, sectionSlug }) => {
  const headline = getStoryHeadline(story);
  const imageUrl =
    get(story, ["hero-image-s3-key"]) ||
    get(story, ["alternative", "home", "default", "hero-image", "hero-image-s3-key"]);

  const externalStoryUrl = isExternalStory(story);
  const href = externalStoryUrl || `/${story?.slug}`;
  const target = externalStoryUrl ? "_blank" : "_self";
  const label = getStoryLabel(story);
  return (
    <Link href={href} target={target}>
      <div styleName="story-card" style={{ border: showSeparator || "none" }}>
        <HeroImage
          story={story}
          widths={[350]}
          headline={headline}
          aspectRatio={[[4, 3]]}
          iconSizes={{ width: 32, height: 32 }}
          imageUrl={imageUrl}
        />

        {label && <Label story={story} />}
        <Headline headline={headline} styleName="headline" sectionSlug={sectionSlug} />
        {showSubHeadline && story?.subheadline && (
          <SubHeadline styleName="subheadline" subHeadline={story?.subheadline} sectionSlug={sectionSlug} />
        )}
        <PublishAndReadTime story={story} showTimestamp={showTimestamp} showReadTime={showReadTime} />
      </div>
    </Link>
  );
};

StoryCard.propTypes = {
  story: object,
  showSeparator: bool,
  showTimestamp: bool,
  showReadTime: bool,
  showSubHeadline: bool,
  sectionSlug: string,
};

export const ThreeColThreeStories = ({ collection, stories, extraProp = "", showStorySeparator = false }) => {
  const {
    show_row_separator,
    show_column_separator,
    show_timestamp,
    show_read_time,
    show_row_title,
    show_subheadline,
    show_row_ad_unit_bottom,
    enable_sponsor_ad,
    sponsor_ad_details,
  } = get(collection, ["associated-metadata"], {});
  const sponsorAdConfig = useSelector((state) => get(state, ["qt", "config", "ads-config", "slots", "sponsor_ad"], {}));
  const bottomAdConfig = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", "row_bottom_ad"], {})
  );
  const rowAdBottom = show_row_ad_unit_bottom?.split("/").slice(-1)[0];
  const rowAdBottomDivId = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "ad_div_mapping", rowAdBottom], "")
  );
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], null));
  const sectionSlug = extraProp;
  if (stories.length === 0) return null;
  return (
    <div className="row-component" styleName="three-col-three-stories">
      {show_row_title && (
        <CollectionTitle
          collection={collection}
          enableSponsorAd={enable_sponsor_ad}
          sponsorAdDetails={sponsor_ad_details}
          sponsorAdConfig={sponsorAdConfig}
          containerStyles={"three-col-three-stories"}
          sectionSlug={sectionSlug}
        />
      )}
      <div styleName="stories-container">
        {stories.map((story) => (
          <StoryCard
            key={story.id}
            story={story}
            showSeparator={show_column_separator || showStorySeparator}
            showTimestamp={show_timestamp}
            showReadTime={show_read_time}
            showSubHeadline={show_subheadline}
            sectionSlug={sectionSlug}
          />
        ))}
      </div>
      {show_row_separator && <RowSeparator />}
      {show_row_ad_unit_bottom && <DfpComponent adStyleName="ad-slot-size-300x250" id={rowAdBottomDivId} />}
    </div>
  );
};

ThreeColThreeStories.propTypes = {
  collection: object,
  stories: array,
  extraProp: string,
};

ThreeColThreeStories.storyLimit = 3;
